import {
  Component,
  OnDestroy,
  OnInit,
  ViewChild,
  signal,
  computed,
  ChangeDetectorRef,
} from '@angular/core';
import { MatDrawer, MatSidenav } from '@angular/material/sidenav';
import { NavigationEnd, Router } from '@angular/router';
import { ReplaySubject, Subject, fromEvent } from 'rxjs';
import { distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { EmailLinkType, User } from 'src/models';
import { SidenavDrawerService } from './core/sidenav-drawer.service';
import { UserService } from './core/user.service';
import { RouterNavigationService } from './core/router-navigation.service';
import { ContainerService } from './core/container.service';
import { OpenWindow } from 'src/helpers';

const REGISTER_URL = 'type=register';
const RESET_PASSWORD_URL = 'password-reset';

/**
 * The main component loaded for the app.
 */
@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit, OnDestroy {
  /** The sidenav displayed on mobile. */
  @ViewChild('sidenav', { static: true })
  sidenav!: MatSidenav;

  /** The component for the drawer. */
  @ViewChild('rightDrawer', { static: true })
  rightDrawer!: MatDrawer;

  /** Destroyed. */
  private destroyed$ = new Subject<void>();

  /** User data to hide/show admin menu. */
  private user$: ReplaySubject<unknown> = new ReplaySubject(1);

  /** The sequence of keys pressed by the user. */
  private sequence: string[];

  /** The code to activate dark mode. */
  private kunamiCode: string[];

  /** Signal with the current url. */
  private url = signal(this.router.url);

  /** Check if the current url is the map or not. */
  isMap = computed(() => this.url().includes('/map'));

  /**
   * Surfaces where the floating Ariadne assistant may appear: the main app views (admin, map,
   * boards, stations, containers, settings…). It is hidden on print/export routes, whose rendered
   * output was inheriting the floating icon. Combined with `userLogged` in the template.
   */
  isMainSurface = computed(() => !this.url().toLowerCase().includes('/print/'));

  /** The previous drawer component. */
  previousDrawerComponent?: MatDrawer;

  /** The page's own drawer, held while the widget history panel borrows the app one. */
  private drawerBeforeWidgetHistory?: MatDrawer;

  /**
   * True while the widget history panel owns the app drawer.
   *
   * Read from the template as well as from the Escape handler: the panel is read-only and
   * sits beside the widget it is about, so it dims nothing — the backdrop is there to catch
   * a click away from the panel, not to put the page behind out of reach.
   */
  widgetHistoryOpen = signal(false);

  /** Used to show top nav. */
  showTopNav = false;

  /** Used to show sidenav. */
  showSideNav = true;

  /** Dark mode enabled. */
  darkEnabled = false;

  /** User login. */
  userLogged = false;

  /** If a dialog is open. */
  isDialogOpen = false;

  /** OpenWindow instance. */
  openWindow: OpenWindow = new OpenWindow();

  constructor(
    public sidenavDrawerService: SidenavDrawerService,
    public userService: UserService,
    private router: Router,
    private navigation: RouterNavigationService,
    private cd: ChangeDetectorRef,
    private containerService: ContainerService,
  ) {
    this.sequence = [];
    this.kunamiCode = [
      'arrowup',
      'arrowup',
      'arrowdown',
      'arrowdown',
      'arrowleft',
      'arrowright',
      'arrowleft',
      'arrowright',
      'b',
      'a',
    ];
    this.tabFocusListen$();
    this.windowResize();
    this.handleKeyboardEvent();
  }

  /**
   * Method to detect if tab is on focus or not.
   */
  tabFocusListen$(): void {
    fromEvent(document, 'visibilitychange')
      .pipe(takeUntil(this.destroyed$))
      .subscribe(() => {
        this.openWindow.tabActive.set(!document.hidden);
        if (this.openWindow.tabActive()) {
          this.containerService.emitToReloadContainersByActionRunnerAndFocusTab();
        }
      });
  }

  /**
   * Subscribe to the dialog open event.
   */
  subscribeIsDialogOpen(): void {
    this.sidenavDrawerService.isDialogOpen$
      .pipe(distinctUntilChanged(), takeUntil(this.destroyed$))
      .subscribe((isOpen) => {
        this.isDialogOpen = isOpen;
        if (this.isDialogOpen) {
          this.previousDrawerComponent =
            this.sidenavDrawerService.drawerComponent();
          this.sidenavDrawerService.checkDrawerComponent() &&
            this.sidenavDrawerService.closeDrawer();
          this.sidenavDrawerService.setDrawer(this.rightDrawer);
        } else if (this.previousDrawerComponent) {
          this.sidenavDrawerService.setDrawer(this.previousDrawerComponent);
        }

        this.cd.detectChanges();
      });
  }

  /**
   * Routes the widget-history context to the app-level drawer.
   *
   * A station widget lives on several surfaces — the dashboard, the station grid, the full
   * view — and each registers a different `MatDrawer`, or none at all. The history panel is
   * the same panel everywhere, so it renders in this component's `#rightDrawer`, which
   * exists on every route. The page's own drawer is put back as soon as the panel closes,
   * so nothing else on that page notices. Same swap the dialog path already does above.
   */
  private subscribeWidgetHistoryContext(): void {
    this.sidenavDrawerService.drawerContext$
      .pipe(takeUntil(this.destroyed$))
      .subscribe((context) => {
        // Cleared here as well as on close, because owning the drawer is what this flag
        // means and the context is what says so. Left to `closedStart` alone it survived a
        // close that drove no animation, and the next drawer to open in this container
        // inherited a panel's worth of styling it had nothing to do with.
        if (context !== 'widgetHistory') {
          this.widgetHistoryOpen.set(false);
          return;
        }
        const current = this.sidenavDrawerService.drawerComponent();
        if (
          this.sidenavDrawerService.checkDrawerComponent() &&
          current !== this.rightDrawer
        ) {
          this.drawerBeforeWidgetHistory = current;
        }
        this.sidenavDrawerService.setDrawer(this.rightDrawer);
        // The history panel is read-only: nothing in it is being edited, so it should be
        // dismissible by clicking away from it or pressing Escape, not only by its own close
        // button. The backdrop is what makes the click-away possible -- without it the drawer
        // has nothing to click outside of on a wide screen, which is where this panel lives.
        this.sidenavDrawerService.setDrawerBackdropForced(true);
        this.sidenavDrawerService.setDisableCloseDrawerOutside(false);
        this.widgetHistoryOpen.set(true);
        this.cd.detectChanges();
      });
  }

  /**
   * Closes the widget history panel on Escape, from wherever the reader's focus happens to be.
   *
   * Material binds its own Escape handler to the drawer element, so it only fires once focus
   * is inside the drawer — and here focus never gets there. The drawer's content is behind a
   * `@defer`, so at the moment it opens there is nothing tabbable in it for Material's
   * `autoFocus` to move to, and focus stays on the button that opened it. The reported
   * symptom is exactly that: Escape worked only after tabbing onto the panel's own close
   * button.
   *
   * Listening on the document rather than fixing the focus is deliberate. Pulling focus into
   * a read-only panel takes it away from whatever the reader was doing, and the panel is
   * meant to be glanced at beside the widget. Escape should dismiss it either way.
   *
   * `defaultPrevented` is what keeps this from stealing an Escape that something above the
   * panel has already answered: a dialog opened over it calls `preventDefault` on the same
   * event, and without this check one keypress would close both.
   */
  private closeWidgetHistoryOnEscape(): void {
    fromEvent<KeyboardEvent>(document, 'keydown')
      .pipe(takeUntil(this.destroyed$))
      .subscribe((event) => {
        if (!this.widgetHistoryOpen()) return;
        if (event.key !== 'Escape' || event.defaultPrevented) return;
        this.sidenavDrawerService.closeDrawer();
      });
  }

  /**
   * Runs whenever the app-level drawer starts closing: settles the service's own state, gives
   * back the backdrop rule the history panel overrode, and returns the drawer the page lent
   * it.
   *
   * The backdrop is released first and unconditionally: it is set for every widget-history
   * open, while the borrowed drawer is only recorded when the page had one of its own, so
   * tying the two together would leave a backdrop under the next drawer to open.
   */
  restoreDrawerAfterWidgetHistory(): void {
    // Fires for every close, including the ones Material drives itself -- the backdrop and
    // Escape -- which is the only place those two can be observed from.
    this.sidenavDrawerService.markDrawerClosed();
    this.sidenavDrawerService.setDrawerBackdropForced(false);
    this.widgetHistoryOpen.set(false);
    if (!this.drawerBeforeWidgetHistory) return;
    this.sidenavDrawerService.setDrawer(this.drawerBeforeWidgetHistory);
    this.drawerBeforeWidgetHistory = undefined;
  }

  /** Subscribe to showTopNav$ from containerService. */
  private subscribeShowTopNav(): void {
    this.containerService.showTopNav$
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (showTopNav: boolean) => {
          this.showTopNav = showTopNav;
          this.cd.detectChanges();
        },
      });
  }

  /**
   * Check the URL path and show/hide the navigation.
   */
  ngOnInit(): void {
    this.subscribeIsDialogOpen();
    this.subscribeWidgetHistoryContext();
    this.closeWidgetHistoryOnEscape();
    this.userLogged = !!this.userService.user().rithmId || false;
    this.userService.setUserData();
    this.userService.userData$.pipe(takeUntil(this.user$)).subscribe((user) => {
      const info = user as User;
      this.userLogged = !!info?.rithmId;
    });

    this.sidenavDrawerService.setSidenav(this.sidenav);
    this.router.events
      .pipe(takeUntil(this.destroyed$))
      .subscribe((routerEvent) => {
        if (routerEvent instanceof NavigationEnd) {
          const path = routerEvent.url;
          const closeNav =
            path !== '' &&
            path !== '/' &&
            path !== '/forgot-password' &&
            path !== '/account-create' &&
            path !== '/password-reset' &&
            path?.toLowerCase().indexOf(REGISTER_URL) === -1 &&
            path?.toLowerCase().indexOf(RESET_PASSWORD_URL) === -1 &&
            path?.toLowerCase().indexOf('type=reset') === -1 &&
            path?.toLowerCase().indexOf('type=invite') === -1;

          this.showTopNav = closeNav;
          this.showSideNav = closeNav;
          this.url.set(path);
        }
      });
    this.validateInvitationExternalLink(window.location.href);
    this.navigation.startSaveRouterHistory$();
    this.subscribeShowTopNav();

    //Sets height using a css variable. this allows us to avoid using vh. Mobile friendly.
    const vh = window.innerHeight * 0.01;
    document.documentElement.style.setProperty('--appvh', `${vh}px`);
  }

  /**
   * Method for detect type and invitation external link in app.
   * @param url Link with Parameters.
   */
  validateInvitationExternalLink(url: string): void {
    const paramsLink = new URLSearchParams(url.split('?')[1]);
    if (
      paramsLink.get('type') &&
      paramsLink.get('type') === EmailLinkType.Invite &&
      paramsLink.get('guid')
    ) {
      this.showTopNav = false;
      this.showSideNav = false;
      this.userService.signOut(true, paramsLink);
    }
  }

  /**
   * Needed to resize a mobile browser when a the scrollbar hides.
   */
  windowResize(): void {
    fromEvent(window, 'resize')
      .pipe(takeUntil(this.destroyed$))
      .subscribe(() => {
        //Sets height using a css variable. this allows us to avoid using vh. Mobile friendly.
        const vh = window.innerHeight * 0.01;
        document.documentElement.style.setProperty('--appvh', `${vh}px`);
      });
  }

  /**
   * Handle key presses from the user to check against the secret code.
   */
  handleKeyboardEvent(): void {
    /*fromEvent(window, 'keydown')
      .pipe(takeUntil(this.destroyed$))
      .subscribe((e) => {
        const ev = e as KeyboardEvent;
        if (ev.key) {
          this.sequence.push(ev.key.toLowerCase());

          if (this.sequence.length > this.kunamiCode.length) {
            this.sequence.shift();
          }

          if (this.isSecretCode()) {
            this.addDarkMode();
          }
        }
      });*/
  }

  /**
   * Determines whether the user has entered the secret code.
   * @returns True if the user has entered the secret code, false otherwise.
   */
  private isSecretCode(): boolean {
    return this.kunamiCode.every(
      (code: string, index: number) => code === this.sequence[index],
    );
  }

  /**
   * Add dark mode class.
   */
  addDarkMode(): void {
    this.darkEnabled = !this.darkEnabled;
  }

  /**
   * Cleanup method.
   */
  ngOnDestroy(): void {
    this.user$.complete();
    this.destroyed$.next();
    this.destroyed$.complete();
    this.navigation.destroyRouterNavigation();
  }
}
